import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAiAssistant } from '../AiAssistantContext';
import * as api from '../api';
import Sidebar from './sidebar/Sidebar';
import TabBar from './content/TabBar';
import TabContent from './content/TabContent';

const Layout: React.FC = () => {
  const { state, dispatch } = useAiAssistant();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(280);
  const dragging = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newWidth = Math.min(Math.max(e.clientX - rect.left, 180), 500);
      setSidebarWidth(newWidth);
    };
    const handleMouseUp = () => {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Auto-switch project when the active tab belongs to a different project
  const selectedProjectRef = useRef(state.selectedProjectId);
  selectedProjectRef.current = state.selectedProjectId;

  useEffect(() => {
    if (!state.activeTabId) return;
    const tab = state.tabs.find((t) => t.id === state.activeTabId);
    if (tab?.projectId && tab.projectId !== selectedProjectRef.current) {
      dispatch({ type: 'SELECT_PROJECT', projectId: tab.projectId });
    }
  }, [state.activeTabId, state.tabs, dispatch]);

  useEffect(() => {
    if (!state.selectedProjectId) {
      dispatch({ type: 'SET_SESSIONS', sessions: [] });
      dispatch({ type: 'SET_FILE_TREE', fileTree: [] });
      return;
    }
    const loadProjectData = async () => {
      dispatch({ type: 'SET_LOADING_SESSIONS', loading: true });
      dispatch({ type: 'SET_LOADING_FILES', loading: true });
      try {
        const [sessions, files] = await Promise.all([
          api.fetchSessions(state.selectedProjectId!),
          api.fetchFileTree(state.selectedProjectId!),
        ]);
        dispatch({ type: 'SET_SESSIONS', sessions });
        dispatch({ type: 'SET_FILE_TREE', fileTree: Array.isArray(files) ? files : [] });
      } catch {
        dispatch({ type: 'SET_LOADING_SESSIONS', loading: false });
        dispatch({ type: 'SET_LOADING_FILES', loading: false });
      }
    };
    loadProjectData();
  }, [state.selectedProjectId, dispatch]);

  return (
    <Container ref={containerRef}>
      <SidebarPane $collapsed={sidebarCollapsed} $width={sidebarWidth}>
        <Sidebar />
      </SidebarPane>
      {!sidebarCollapsed && <ResizeHandle onMouseDown={handleMouseDown} />}
      <ContentPane>
        <CollapseToggle
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
        >
          <FontAwesomeIcon icon={sidebarCollapsed ? faChevronRight : faChevronLeft} />
        </CollapseToggle>
        <TabBar />
        <TabContent />
      </ContentPane>
    </Container>
  );
};

export default Layout;

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const SidebarPane = styled.div<{ $collapsed: boolean; $width: number }>`
  width: ${(p) => (p.$collapsed ? '0px' : `${p.$width}px`)};
  min-width: ${(p) => (p.$collapsed ? '0px' : `${p.$width}px`)};
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fafafa;
  transition: ${(p) => (p.$collapsed ? 'width 0.2s, min-width 0.2s' : 'none')};
  overflow: hidden;
`;

const ResizeHandle = styled.div`
  width: 4px;
  cursor: col-resize;
  background: transparent;
  flex-shrink: 0;
  border-right: 1px solid #ddd;
  &:hover {
    background: #d0d0d0;
  }
`;

const ContentPane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  position: relative;
`;

const CollapseToggle = styled.button`
  position: absolute;
  top: 8px;
  left: 0;
  width: 20px;
  height: 24px;
  border-radius: 0 4px 4px 0;
  border: 1px solid #ddd;
  border-left: none;
  background: #fafafa;
  color: #888;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  z-index: 10;
  &:hover {
    background: #eee;
    color: #555;
  }
`;
